import search from '@jetbrains/icons/search.svg'
import Input, {Size as InputSize} from '@jetbrains/ring-ui/components/input/input'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import classNames from 'classnames'
import * as React from 'react'

import {isEventWithSpecialKeyPressed} from '../../../utils/keys'
import {useHint} from '../../packages/Hints/Hints.hooks'
import {HINT_CATEGORY, stringifyHintId, toHintId} from '../../packages/Hints/Hints.types'
import type {Hint} from '../../packages/Hints/Hints.types'
import {useSearchShortcutSidebar} from '../../packages/Shortcuts/hooks/useSearchShortcutSidebar'

import type {Props, SidebarSearchOwnProps, State} from './SidebarSearch.types'

import styles from './SidebarSearch.css'

const searchInputHint: Hint = {
  id: toHintId('sidebar-search-input'),
  name: 'Search',
  text: 'Search projects and build configurations by their name. Press the Q key to quickly switch to searching.',
  category: HINT_CATEGORY.sidebar,
  offset: 20,
  directions: [Directions.BOTTOM_RIGHT, Directions.RIGHT_BOTTOM],
}

const SEARCH_THROTTLE = 200
const LONG_SEARCH_THROTTLE = 500

class Search extends React.PureComponent<Props, State> {
  static getDerivedStateFromProps(props: Props, state: State) {
    if (!props.searchActive && state.query) {
      return {
        query: '',
      }
    }

    return null
  }

  state: State = {
    query: this.props.searchQuery || '', // query stored in state and redux store for better performance
  }

  inputEl: HTMLInputElement | null | undefined
  changeTimeout: number | null | undefined
  componentDidMount() {
    document.addEventListener('keydown', this.onGlobalKeyEvent, true)
    document.addEventListener('keyup', this.onGlobalKeyEvent, true)
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.searchActive && !this.props.searchActive && this.inputEl) {
      this.inputEl.blur()
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onGlobalKeyEvent, true)
    document.removeEventListener('keyup', this.onGlobalKeyEvent, true)
    document.removeEventListener('click', this.onDocumentClick)
  }

  onChange: (arg0: React.SyntheticEvent<HTMLInputElement>) => void = event => {
    const query = event.currentTarget.value || ''
    this.setState({
      query,
    })
    const cleanedQuery = query.trim()

    if (this.changeTimeout) {
      clearTimeout(this.changeTimeout)
    }

    if (!cleanedQuery.length || process.env.NODE_ENV === 'test') {
      this.props.changeSidebarSearchQuery?.(cleanedQuery)
    } else {
      this.changeTimeout = window.setTimeout(
        () => {
          this.props.changeSidebarSearchQuery?.(cleanedQuery)
        },
        cleanedQuery.length > 2 ? SEARCH_THROTTLE : LONG_SEARCH_THROTTLE,
      )
    }
  }

  onClear: () => void = () => {
    this.setState({
      query: '',
    })

    if (this.inputEl) {
      this.inputEl.blur()
    }

    this.closeSearch()
  }

  onDocumentClick: (arg0: MouseEvent) => void = event => {
    if (!this.state.query && event.target instanceof HTMLElement && event.target !== this.inputEl) {
      const interactiveTagNames = ['INPUT', 'TEXTAREA', 'A', 'BUTTON', 'LABEL']
      let interactiveFound = false
      let clickIsInsideSidebar = false
      let el: HTMLElement | null = event.target

      while (el != null) {
        if (!interactiveFound && interactiveTagNames.includes(el.tagName.toUpperCase())) {
          interactiveFound = true
        }

        if (this.props.sidebarRef?.current === el || el.getAttribute('data-inside-sidebar')) {
          clickIsInsideSidebar = true
          el = null
        } else {
          el = el.parentElement
        }
      }

      if ((clickIsInsideSidebar && !interactiveFound) || !clickIsInsideSidebar) {
        this.closeSearch()
      }
    }
  }

  onFocus: () => void = () => {
    this.props.changeSidebarSearchActive?.(true)
    document.addEventListener('click', this.onDocumentClick)
  }

  onKeyDown: (arg0: React.KeyboardEvent<HTMLInputElement>) => void = event => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowLeft':
        event.stopPropagation()
        return

      case 'Home':
        event.preventDefault()
        event.stopPropagation()
        event.currentTarget.setSelectionRange(
          0,
          event.shiftKey ? event.currentTarget.selectionEnd : 0,
        )
        return

      case 'End':
        event.preventDefault()
        event.stopPropagation()
        const position = event.currentTarget.value.length
        event.currentTarget.setSelectionRange(
          event.shiftKey ? event.currentTarget.selectionStart : position,
          position,
        )
        return

      default:
        return
    }
  }

  onGlobalKeyEvent: (arg0: KeyboardEvent) => void = event => {
    if (
      (event.type === 'keyup' &&
        (event.code === 'KeyQ' || event.key === 'q') &&
        !isEventWithSpecialKeyPressed(event)) ||
      (event.type === 'keydown' &&
        this.props.isSearchShortcutEnabled &&
        (event.code === 'KeyK' || event.key === 'k') &&
        (event.metaKey || event.ctrlKey))
    ) {
      if (event.target instanceof HTMLElement) {
        if (
          event.target.matches('input:not([type=checkbox]),select,textarea') ||
          event.target.contentEditable === 'true'
        ) {
          return
        }
      }

      event.stopPropagation()
      setTimeout(() => {
        if (this.inputEl) {
          this.inputEl.focus()
        }
      }, 0)
    }

    if (event.type === 'keydown' && event.key === 'Escape') {
      if (this.inputEl) {
        this.inputEl.blur()
      }

      if (this.props.searchActive) {
        this.props.onEscape?.()
      }

      if (event.target === this.inputEl && this.props.propagateEscape !== true) {
        event.stopPropagation()
      }

      this.closeSearch()
      this.setState({
        query: '',
      })
    }
  }

  setInputRef: (arg0: HTMLInputElement | null | undefined) => void = el => {
    const {inputRef} = this.props

    if (inputRef != null) {
      inputRef(el)
    }

    this.inputEl = el
  }

  closeSearch() {
    document.removeEventListener('click', this.onDocumentClick)
    this.props.changeSidebarSearchActive?.(false)
  }

  render(): React.ReactNode {
    const {
      className,
      placeholder,
      wrapperClassName,
      selectedItemId,
      collapsed,
      inputRef,
      propagateEscape,
      changeSidebarSearchActive,
      onEscape,
      searchActive,
      sidebarRef,
      searchQuery,
      changeSidebarSearchQuery,
      isSearchShortcutEnabled,
      ...restProps
    } = this.props

    const classes = classNames(styles.search, className, {
      [styles.hidden]: collapsed === true,
      [styles.hotkeyHint]: this.state.query === '',
    })

    return (
      <div
        className={classNames(styles.wrapper, wrapperClassName)}
        data-hint-container-id={stringifyHintId(searchInputHint.id)}
      >
        <Input
          id={'search-projects'}
          data-test={'sidebar-search'}
          placeholder={placeholder ?? 'Jump to...'}
          aria-label={'Jump to... (Q)'}
          autoComplete="off"
          aria-activedescendant={collapsed ? undefined : selectedItemId}
          icon={search}
          inputRef={this.setInputRef}
          className={classes}
          value={this.state.query}
          onFocus={this.onFocus}
          onChange={this.onChange}
          onClear={this.onClear}
          onKeyDown={this.onKeyDown}
          size={InputSize.FULL}
          {...restProps}
        />
      </div>
    )
  }
}

export default function SidebarSearch(props: SidebarSearchOwnProps) {
  const {collapsed} = props

  useHint(searchInputHint)
  const isSearchShortcutEnabled = useSearchShortcutSidebar()

  return (
    <Search isSearchShortcutEnabled={isSearchShortcutEnabled} collapsed={collapsed} {...props} />
  )
}
